import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, BookOpen, Heart, ArrowUpRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import TeamMemberAvatar from "../components/TeamMemberAvatar";
import { personSchema } from "../data/teamData";
import { useTeamData } from "../lib/useTeamData";

export default function Team() {
    const { founder, leadership } = useTeamData();
    // Compose multi-Person + AboutPage schema graph from LIVE data
    const peopleGraph = [
        personSchema({
            name: founder.name,
            role: founder.role,
            url: "https://anandtutorials.com/founder",
            sameAs: [],
            bio: founder.bio,
        }),
        ...leadership.map((p) =>
            personSchema({
                name: p.name,
                role: p.role,
                url: `https://anandtutorials.com/team#${p.slug}`,
                bio: p.bio,
            })
        ),
    ];

    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            ...peopleGraph,
            {
                "@type": "AboutPage",
                "url": "https://anandtutorials.com/team",
                "name": "The Founding Team — Four Pillars of Anand Tutorials",
                "description": "Anand Tutorials is run by four Rajput brothers — Anand Rajput (Founder & MD), Nitesh Rajput (COO & Technology), Aditya Rajput (Operations & Student Success), Nilesh Rajput (Finance). Named, accountable family-led leadership since 2013.",
            },
        ],
    };

    return (
        <div data-testid="team-page">
            <SEO
                title="The Founding Team — Four Pillars of Anand Tutorials"
                description="Anand Tutorials is run by four Rajput brothers — Anand (Founder & MD), Nitesh (COO & Technology), Aditya (Operations & Student Success), Nilesh (Finance). Named, accountable family-led leadership since 2013."
                keywords="Anand Tutorials team, Anand Rajput, Nitesh Rajput, Aditya Rajput, Nilesh Rajput, Anand Tutorials founding team, four pillars Anand Tutorials, home tuition team India, education team Jabalpur, COO Anand Tutorials, Finance Director Anand Tutorials"
                path="/team"
                schema={schema}
            />

            <PageHeader
                eyebrow="The Four Pillars of Anand Tutorials"
                title="A family-led institution. Named pillars. Direct accountability."
                subtitle="Anand Tutorials is run by four Rajput brothers — Anand, Nitesh, Aditya and Nilesh — each owning a critical pillar of the business. Every parent escalation, every tutor decision, every finance question routes to a named person with a phone you can ring."
            >
                <div className="flex flex-wrap gap-3">
                    <Link to="/register" data-testid="team-cta-demo" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        Book a Free 1:1 Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href="tel:+917000530316" data-testid="team-cta-call" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/15">
                        <Phone className="w-4 h-4" /> Call Us
                    </a>
                </div>
            </PageHeader>

            <div className="container mx-auto px-4 py-12 lg:py-16 max-w-6xl">

                {/* Founder spotlight — pillar 1 */}
                <section className="mb-14" data-testid="team-founder-card">
                    <div className="text-[10px] font-bold tracking-widest text-amber-600 uppercase mb-2">Pillar 1 of 4 · Vision & Direction</div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] mb-6">Founder &amp; Managing Director</h2>
                    <Link
                        to="/founder"
                        className="group block bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white rounded-2xl p-8 md:p-10 hover:shadow-2xl transition-shadow"
                    >
                        <div className="grid md:grid-cols-[120px_1fr_auto] gap-6 items-center">
                            <div className="flex justify-center md:justify-start">
                                <TeamMemberAvatar
                                    assetKey={founder.photo_asset_key || "founder_photo"}
                                    size="w-32 h-32 md:w-36 md:h-36"
                                    priority
                                    alt={`${founder.name} — ${founder.role}`}
                                    ringClass="ring-4 ring-amber-300/60 shadow-2xl"
                                    fallback={<span className="text-4xl font-heading font-bold text-[#0F172A]">{(founder.name || "A").split(" ")[0].charAt(0)}</span>}
                                />
                            </div>
                            <div>
                                <div className="text-xs font-bold text-amber-300 uppercase tracking-wider mb-1">Founder · Director · Operator</div>
                                <h3 className="font-heading text-2xl md:text-3xl font-bold mb-2">{founder.name}</h3>
                                <p className="text-blue-100 leading-relaxed">{(founder.bio || "").slice(0, 220)}…</p>
                            </div>
                            <div className="inline-flex items-center gap-1 text-amber-300 font-semibold group-hover:translate-x-1 transition-transform">
                                Read the full story <ArrowUpRight className="w-4 h-4" />
                            </div>
                        </div>
                    </Link>
                </section>

                {/* Other three pillars */}
                <section className="mb-14">
                    <div className="text-[10px] font-bold tracking-widest text-amber-600 uppercase mb-2">Pillars 2 · 3 · 4</div>
                    <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
                        <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A]">The Founding Team</h2>
                        <p className="text-sm text-slate-500">Three named directors · each owns a critical pillar</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {leadership.map((p, idx) => (
                            <div
                                key={p.id || p.slug}
                                id={p.slug}
                                data-testid={`team-member-${p.slug}`}
                                className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-md transition-all"
                            >
                                <div className="mb-4 flex items-center gap-4">
                                    <TeamMemberAvatar
                                        assetKey={p.photo_asset_key || `team_photo_${p.slug}`}
                                        size="w-16 h-16"
                                        alt={`${p.name} — ${p.role}`}
                                        ringClass="ring-2 ring-white shadow"
                                        fallback={
                                            <div className={`flex items-center justify-center ${idx === 0 ? "text-purple-600" : idx === 1 ? "text-rose-600" : "text-emerald-600"}`}>
                                                {idx === 0 && <BookOpen className="w-7 h-7" />}
                                                {idx === 1 && <Heart className="w-7 h-7" />}
                                                {idx === 2 && <ShieldCheck className="w-7 h-7" />}
                                            </div>
                                        }
                                    />
                                    <div className="min-w-0">
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{(p.role || "").split("—")[0].trim()}</div>
                                        <h3 className="font-heading font-bold text-lg text-[#0F172A] leading-snug truncate">{p.name}</h3>
                                    </div>
                                </div>
                                <p className="text-sm text-slate-600 leading-relaxed mb-4">{p.bio}</p>
                                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Key Responsibilities</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {(p.responsibilities || []).map((e, i) => (
                                        <span key={i} className="text-[10px] font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                                            {e}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Why this matters */}
                <section className="bg-slate-50 rounded-2xl p-8 md:p-10 mb-14">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] mb-3">Why we publish the team — not just the brand</h2>
                    <p className="text-slate-700 leading-relaxed mb-4">
                        Tutor-marketplace apps deliberately hide their team — there&apos;s no one to escalate to. Anand Tutorials does the opposite. Four named Rajput pillars — Anand (Vision &amp; Direction), Nitesh (Operations &amp; Technology), Aditya (Student Success &amp; Tutor Matching), and Nilesh (Finance &amp; Accounts). Every parent escalation, every tutor decision, every finance question routes to one of them, with a phone number. That&apos;s the difference between a brand and an institution.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <div className="font-bold text-sm text-[#0F172A] mb-1">Family-led accountability</div>
                            <div className="text-xs text-slate-600">All four directors are Rajputs — long-term ownership, no rotating management.</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <div className="font-bold text-sm text-[#0F172A] mb-1">Founder reads escalations</div>
                            <div className="text-xs text-slate-600">Any escalation above standard process is read personally by Anand Rajput within 48 hours.</div>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section data-testid="team-cta-section" className="bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white rounded-2xl p-8 md:p-10 text-center">
                    <h3 className="font-heading text-2xl md:text-3xl font-bold mb-3">Meet a tutor verified by this team</h3>
                    <p className="text-blue-100 mb-6 max-w-2xl mx-auto">The free demo is with a tutor who passed 4 independent checks signed off by the people above. No card. No commitment.</p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <Link to="/register" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-6 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                            Book a Free Demo Class <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/about" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/15">
                            About Anand Tutorials <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </section>

            </div>
        </div>
    );
}
